import { inventoryRulesLocales } from "./inventoryRulesLocales";
import { organizationLocales } from "./organizationLocales";
import { planningLocales } from "./planningLocales";
import { complianceLocales } from "./complianceLocales";
import { portabilityLocales } from "./portabilityLocales";
import { warehouseReportLocales } from "./warehouseReportLocales";
import { scanFillLocales } from "./scanFillLocales";
import { aiAnalysisLocales } from "./aiAnalysisLocales";
import { companyLicenseLocales } from "./companyLicenseLocales";
import { settingsTextLocales } from "./settingsTextLocales";
import { coreTextLocales } from "./coreTextLocales";

/**
 * Inventory depth (plan 3.1–3.12): units, variants, location hierarchy,
 * quantity types, quarantine, receipt tolerance, lot rules, traceability.
 */
const baseLocales = {
  en: {
    nav: { stockOverview: "Stock Overview", traceability: "Traceability" },
    lotTracking: { status_quarantine: "Quarantine", status_rejected: "Rejected" },
    stock: {
      unit: "Unit",
      lot: "Lot",
      serial: "Serial no.",
      serialsHint: "Serial numbers (comma or new line)",
      productionDate: "Production date",
      expiryDate: "Expiry date",
      location: "Location",
      parentLocation: "Parent",
      root: "warehouse root",
      level: "Level",
      locationType: "Location type",
      noLocation: "No location",
      autoLocation: "Automatic (receiving / quarantine)",
      unlocated: "Not assigned to a location",
      capacity: "Capacity",
      capacityUnits: "Units",
      capacityVolume: "Volume m³",
      capacityWeight: "Weight kg",
      capacityShort: { units: "Units", volumeM3: "m³", weightKg: "kg" },
      capacityExceeded: "Location {{code}} would exceed its capacity.",
      sameLocation: "Source and destination are the same location.",
      inTransitTransfer: "Goods travel between warehouses (book as in transit until received)",
      blindReceipt: "Blind receipt",
      blind: "No order",
      deliveryNoteNo: "Delivery note no.",
      addLine: "Add line",
      tolerance: "Tolerance",
      toQuarantine: "To quarantine",
      dispatch: "Dispatch",
      dispatchLots: "Lots and serials to ship",
      dispatchErrors: {
        LOT_REQUIRED: "Enter the shipped lot for {{sku}}.",
        SERIALS_REQUIRED: "Enter one serial number per unit for {{sku}}."
      },
      uom: { adet: "Piece", koli: "Case", paket: "Pack", palet: "Pallet", kg: "Kilogram", g: "Gram", lt: "Litre", ml: "Millilitre", m: "Metre" },
      levels: { zone: "Zone", aisle: "Aisle", rack: "Rack", bin: "Bin" },
      types: {
        receiving: "Receiving",
        storage: "Storage",
        quarantine: "Quarantine",
        shipping: "Shipping",
        returns: "Returns",
        physical: "Physical",
        reserved: "Reserved",
        inTransit: "In transit",
        available: "Available"
      },
      locationErrors: {
        codeRequired: "Code is required.",
        invalidLevel: "Choose a level.",
        invalidType: "Choose a location type.",
        parentWarehouse: "The parent belongs to another warehouse.",
        levelOrder: "The level must be below the parent's level.",
        capacity: "Capacity values must be zero or more."
      },
      lotErrors: {
        lotRequired: "This product is lot-tracked; enter a lot number.",
        serialDuplicate: "Serial numbers must be unique.",
        serialCount: "Enter one serial number per unit.",
        productionDateRequired: "Production date is required for this product.",
        expiryRequired: "Expiry date is required for this product.",
        expiryBeforeProduction: "Expiry date must be after the production date."
      },
      receiptErrors: {
        PRODUCT_NOT_FOUND: "Product not found.",
        UNKNOWN_UOM: "The product has no such unit.",
        OVER_TOLERANCE: "Over the order beyond tolerance (max {{max}}, ±{{pct}}%).",
        QUARANTINE_LOCATION_REQUIRED: "This product needs a quarantine location in the warehouse.",
        LOCATION_CAPACITY: "The location would exceed its capacity."
      },
      toleranceStatus: { complete: "Complete", under: "Short", over: "Over (within tolerance)", overLimit: "Over the limit" },
      settings: {
        title: "Units, quality and lot rules",
        altUnits: "Alternative units (base: {{base}})",
        factor: "How many {{base}}",
        addUnit: "Add unit",
        qualityRequired: "Quality inspection on receipt (quarantine)",
        tolerance: "Receipt tolerance %",
        tracking: "Tracking",
        trackingModes: { none: "None", lot: "Lot", serial: "Serial" },
        shelfLifeDays: "Shelf life (days)",
        shelfLife: "Shelf life {{days}} days",
        requireProductionDate: "Production date required",
        requireExpiry: "Expiry date required",
        errors: {
          required: "Choose a unit.",
          duplicate: "Each unit can be added once.",
          positive: "Enter a value greater than zero.",
          range: "Tolerance must be between 0 and 100.",
          failed: "Could not save."
        }
      },
      variants: {
        title: "Variants",
        attribute: "Attribute (e.g. Colour)",
        values: "Values, comma separated",
        addAttribute: "Add attribute",
        generate: "Create {{count}} variants",
        matrix: "{{count}} combinations",
        tooMany: "Too many combinations (max 200).",
        none: "No variants yet.",
        variant: "Variant",
        total: "Total",
        variantOf: "Variant of",
        failed: "Variants could not be created."
      },
      migration: {
        pending: "{{updates}} locations still use the old aisle / rack / bin fields; {{creates}} parent records will be created.",
        run: "Move to hierarchy",
        done: "Hierarchy created: {{created}} new, {{updated}} moved.",
        legacy: "Old format"
      },
      overview: {
        title: "Stock overview",
        subtitle: "Physical, reserved, available, quarantined and in-transit stock by warehouse and location",
        search: "SKU or name",
        allWarehouses: "All warehouses",
        allTypes: "All location types",
        refresh: "Refresh",
        empty: "No stock found.",
        byProduct: "By product"
      },
      transit: {
        title: "In transit",
        none: "Nothing in transit.",
        route: "Route",
        receive: "Receive",
        received: "Transfer received."
      },
      quarantine: {
        title: "Quality decision",
        decide: "Decide",
        inspection: "Inspection",
        noInspection: "No inspection for this product yet — record one first.",
        decision: "Decision",
        release: "Release to storage",
        return: "Move to returns",
        apply: "Apply",
        done: { release: "Released to storage.", return: "Moved to the returns location." },
        errors: {
          invalidDecision: "Choose a decision.",
          inspectionRequired: "Choose the inspection the decision is based on.",
          inspectionRejected: "This inspection record was rejected.",
          inspectionProduct: "The inspection belongs to another product.",
          inspectionLot: "The inspection belongs to another lot.",
          inspectionNotPassed: "Only a passed inspection can release stock.",
          inspectionPassed: "A passed inspection cannot send stock to returns.",
          NOT_QUARANTINE_LOCATION: "The stock is not in a quarantine location.",
          STORAGE_LOCATION_REQUIRED: "The warehouse needs a storage location.",
          RETURNS_LOCATION_REQUIRED: "The warehouse needs a returns location."
        }
      },
      trace: {
        title: "Traceability and recall",
        subtitle: "Trace a lot or serial from supplier to customer and list affected customers",
        anyProduct: "Any product",
        run: "Trace",
        open: "Trace",
        queryRequired: "Enter a lot or a serial number.",
        empty: "No records for this lot / serial.",
        backward: "Backward",
        forward: "Forward",
        suppliers: "Suppliers",
        receipts: "Goods receipts",
        quality: "Quality inspections",
        inputLots: "Input lots",
        lotCard: "Lot",
        production: "Production",
        timeline: "Timeline",
        outputLots: "Used in lots",
        packaging: "Packaging",
        shipments: "Shipments",
        recallTitle: "Recall list",
        noCustomers: "No shipments to customers for this lot.",
        stages: {
          receipt: "Receipt",
          quality: "Quality",
          production: "Production",
          consumption: "Consumed",
          packaging: "Packaging",
          shipment: "Shipment"
        },
        cols: {
          customer: "Customer",
          contact: "Contact",
          email: "E-mail",
          phone: "Phone",
          address: "Address",
          quantity: "Quantity",
          orders: "Orders",
          serials: "Serials",
          lastShipDate: "Last shipment"
        }
      }
    }
  },
  tr: {
    nav: { stockOverview: "Anlık Stok", traceability: "İzlenebilirlik" },
    lotTracking: { status_quarantine: "Karantina", status_rejected: "Reddedildi" },
    stock: {
      unit: "Birim",
      lot: "Lot",
      serial: "Seri no",
      serialsHint: "Seri numaraları (virgül veya yeni satır)",
      productionDate: "Üretim tarihi",
      expiryDate: "Son kullanma tarihi",
      location: "Lokasyon",
      parentLocation: "Üst lokasyon",
      root: "depo kökü",
      level: "Seviye",
      locationType: "Lokasyon tipi",
      noLocation: "Lokasyon yok",
      autoLocation: "Otomatik (kabul / karantina)",
      unlocated: "Lokasyona atanmamış",
      capacity: "Kapasite",
      capacityUnits: "Adet",
      capacityVolume: "Hacim m³",
      capacityWeight: "Ağırlık kg",
      capacityShort: { units: "Adet", volumeM3: "m³", weightKg: "kg" },
      capacityExceeded: "{{code}} lokasyonunun kapasitesi aşılır.",
      sameLocation: "Kaynak ve hedef aynı lokasyon.",
      inTransitTransfer: "Mal depolar arasında taşınıyor (teslim alınana kadar yolda say)",
      blindReceipt: "Kör mal kabul",
      blind: "Siparişsiz",
      deliveryNoteNo: "İrsaliye no",
      addLine: "Satır ekle",
      tolerance: "Tolerans",
      toQuarantine: "Karantinaya",
      dispatch: "Sevk et",
      dispatchLots: "Sevk edilecek lot ve seriler",
      dispatchErrors: {
        LOT_REQUIRED: "{{sku}} için sevk edilen lotu girin.",
        SERIALS_REQUIRED: "{{sku}} için her birime bir seri numarası girin."
      },
      uom: { adet: "Adet", koli: "Koli", paket: "Paket", palet: "Palet", kg: "Kilogram", g: "Gram", lt: "Litre", ml: "Mililitre", m: "Metre" },
      levels: { zone: "Bölge", aisle: "Koridor", rack: "Raf", bin: "Göz" },
      types: {
        receiving: "Kabul",
        storage: "Depolama",
        quarantine: "Karantina",
        shipping: "Sevk",
        returns: "İade",
        physical: "Fiziki",
        reserved: "Rezerve",
        inTransit: "Yolda",
        available: "Kullanılabilir"
      },
      locationErrors: {
        codeRequired: "Kod zorunludur.",
        invalidLevel: "Bir seviye seçin.",
        invalidType: "Bir lokasyon tipi seçin.",
        parentWarehouse: "Üst lokasyon başka bir depoya ait.",
        levelOrder: "Seviye, üst lokasyonun seviyesinin altında olmalı.",
        capacity: "Kapasite değerleri sıfır veya daha büyük olmalı."
      },
      lotErrors: {
        lotRequired: "Bu ürün lot takipli; lot numarası girin.",
        serialDuplicate: "Seri numaraları tekrarlanamaz.",
        serialCount: "Her birim için bir seri numarası girin.",
        productionDateRequired: "Bu ürün için üretim tarihi zorunlu.",
        expiryRequired: "Bu ürün için son kullanma tarihi zorunlu.",
        expiryBeforeProduction: "Son kullanma tarihi üretim tarihinden sonra olmalı."
      },
      receiptErrors: {
        PRODUCT_NOT_FOUND: "Ürün bulunamadı.",
        UNKNOWN_UOM: "Ürünün böyle bir birimi yok.",
        OVER_TOLERANCE: "Sipariş toleransın üzerinde aşılıyor (en çok {{max}}, ±%{{pct}}).",
        QUARANTINE_LOCATION_REQUIRED: "Bu ürün için depoda bir karantina lokasyonu gerekli.",
        LOCATION_CAPACITY: "Lokasyonun kapasitesi aşılır."
      },
      toleranceStatus: { complete: "Tam", under: "Eksik", over: "Fazla (tolerans içinde)", overLimit: "Tolerans aşıldı" },
      settings: {
        title: "Birim, kalite ve lot kuralları",
        altUnits: "Alternatif birimler (temel: {{base}})",
        factor: "Kaç {{base}}",
        addUnit: "Birim ekle",
        qualityRequired: "Kabulde kalite muayenesi (karantina)",
        tolerance: "Kabul toleransı %",
        tracking: "Takip",
        trackingModes: { none: "Yok", lot: "Lot", serial: "Seri" },
        shelfLifeDays: "Raf ömrü (gün)",
        shelfLife: "Raf ömrü {{days}} gün",
        requireProductionDate: "Üretim tarihi zorunlu",
        requireExpiry: "SKT zorunlu",
        errors: {
          required: "Bir birim seçin.",
          duplicate: "Her birim bir kez eklenebilir.",
          positive: "Sıfırdan büyük bir değer girin.",
          range: "Tolerans 0 ile 100 arasında olmalı.",
          failed: "Kaydedilemedi."
        }
      },
      variants: {
        title: "Varyantlar",
        attribute: "Öznitelik (ör. Renk)",
        values: "Değerler, virgülle",
        addAttribute: "Öznitelik ekle",
        generate: "{{count}} varyant oluştur",
        matrix: "{{count}} kombinasyon",
        tooMany: "Kombinasyon sayısı çok fazla (en çok 200).",
        none: "Henüz varyant yok.",
        variant: "Varyant",
        total: "Toplam",
        variantOf: "Ana ürün",
        failed: "Varyantlar oluşturulamadı."
      },
      migration: {
        pending: "{{updates}} lokasyon hâlâ eski koridor / raf / göz alanlarını kullanıyor; {{creates}} üst kayıt oluşturulacak.",
        run: "Hiyerarşiye taşı",
        done: "Hiyerarşi oluşturuldu: {{created}} yeni, {{updated}} taşındı.",
        legacy: "Eski biçim"
      },
      overview: {
        title: "Anlık stok",
        subtitle: "Depo ve lokasyon bazında fiziki, rezerve, kullanılabilir, karantinadaki ve yoldaki stok",
        search: "SKU veya ad",
        allWarehouses: "Tüm depolar",
        allTypes: "Tüm lokasyon tipleri",
        refresh: "Yenile",
        empty: "Stok bulunamadı.",
        byProduct: "Ürün bazında"
      },
      transit: {
        title: "Yoldaki",
        none: "Yolda mal yok.",
        route: "Güzergâh",
        receive: "Teslim al",
        received: "Transfer teslim alındı."
      },
      quarantine: {
        title: "Kalite kararı",
        decide: "Karar ver",
        inspection: "Muayene",
        noInspection: "Bu ürün için muayene yok — önce muayene kaydı girin.",
        decision: "Karar",
        release: "Serbest bırak (depolama)",
        return: "İade lokasyonuna taşı",
        apply: "Uygula",
        done: { release: "Depolamaya serbest bırakıldı.", return: "İade lokasyonuna taşındı." },
        errors: {
          invalidDecision: "Bir karar seçin.",
          inspectionRequired: "Kararın dayandığı muayeneyi seçin.",
          inspectionRejected: "Bu muayene kaydı reddedilmiş.",
          inspectionProduct: "Muayene başka bir ürüne ait.",
          inspectionLot: "Muayene başka bir lota ait.",
          inspectionNotPassed: "Yalnızca geçen bir muayene stoğu serbest bırakabilir.",
          inspectionPassed: "Geçen bir muayene stoğu iadeye gönderemez.",
          NOT_QUARANTINE_LOCATION: "Stok karantina lokasyonunda değil.",
          STORAGE_LOCATION_REQUIRED: "Depoda bir depolama lokasyonu gerekli.",
          RETURNS_LOCATION_REQUIRED: "Depoda bir iade lokasyonu gerekli."
        }
      },
      trace: {
        title: "İzlenebilirlik ve geri çağırma",
        subtitle: "Lot veya seriyi tedarikçiden müşteriye izleyin, etkilenen müşterileri listeleyin",
        anyProduct: "Tüm ürünler",
        run: "İzle",
        open: "İzle",
        queryRequired: "Lot veya seri numarası girin.",
        empty: "Bu lot / seri için kayıt yok.",
        backward: "Geriye",
        forward: "İleriye",
        suppliers: "Tedarikçiler",
        receipts: "Mal kabuller",
        quality: "Kalite muayeneleri",
        inputLots: "Girdi lotları",
        lotCard: "Lot",
        production: "Üretim",
        timeline: "Zaman çizelgesi",
        outputLots: "Kullanıldığı lotlar",
        packaging: "Paketleme",
        shipments: "Sevkiyatlar",
        recallTitle: "Geri çağırma listesi",
        noCustomers: "Bu lot müşterilere sevk edilmemiş.",
        stages: {
          receipt: "Kabul",
          quality: "Kalite",
          production: "Üretim",
          consumption: "Tüketim",
          packaging: "Paketleme",
          shipment: "Sevk"
        },
        cols: {
          customer: "Müşteri",
          contact: "Yetkili",
          email: "E-posta",
          phone: "Telefon",
          address: "Adres",
          quantity: "Miktar",
          orders: "Siparişler",
          serials: "Seriler",
          lastShipDate: "Son sevk"
        }
      }
    }
  },
  ar: {
    nav: { stockOverview: "المخزون الفوري", traceability: "التتبع" },
    lotTracking: { status_quarantine: "حجر", status_rejected: "مرفوض" },
    stock: {
      unit: "الوحدة",
      lot: "الدفعة",
      serial: "الرقم التسلسلي",
      serialsHint: "الأرقام التسلسلية (فاصلة أو سطر جديد)",
      productionDate: "تاريخ الإنتاج",
      expiryDate: "تاريخ الانتهاء",
      location: "الموقع",
      parentLocation: "الموقع الأعلى",
      root: "جذر المستودع",
      level: "المستوى",
      locationType: "نوع الموقع",
      noLocation: "بدون موقع",
      autoLocation: "تلقائي (استلام / حجر)",
      unlocated: "غير مخصص لموقع",
      capacity: "السعة",
      capacityUnits: "قطع",
      capacityVolume: "الحجم م³",
      capacityWeight: "الوزن كغ",
      capacityShort: { units: "قطع", volumeM3: "م³", weightKg: "كغ" },
      capacityExceeded: "سيتجاوز الموقع {{code}} سعته.",
      sameLocation: "المصدر والوجهة هما الموقع نفسه.",
      inTransitTransfer: "البضاعة تنتقل بين المستودعات (تُحسب في الطريق حتى الاستلام)",
      blindReceipt: "استلام بدون أمر",
      blind: "بدون أمر",
      deliveryNoteNo: "رقم إشعار التسليم",
      addLine: "إضافة سطر",
      tolerance: "التفاوت",
      toQuarantine: "إلى الحجر",
      dispatch: "شحن",
      dispatchLots: "الدفعات والأرقام التسلسلية للشحن",
      dispatchErrors: {
        LOT_REQUIRED: "أدخل الدفعة المشحونة لـ {{sku}}.",
        SERIALS_REQUIRED: "أدخل رقمًا تسلسليًا لكل وحدة لـ {{sku}}."
      },
      uom: { adet: "قطعة", koli: "كرتونة", paket: "عبوة", palet: "منصة", kg: "كيلوغرام", g: "غرام", lt: "لتر", ml: "مليلتر", m: "متر" },
      levels: { zone: "منطقة", aisle: "ممر", rack: "رف", bin: "خانة" },
      types: {
        receiving: "استلام",
        storage: "تخزين",
        quarantine: "حجر",
        shipping: "شحن",
        returns: "مرتجعات",
        physical: "فعلي",
        reserved: "محجوز",
        inTransit: "في الطريق",
        available: "متاح"
      },
      locationErrors: {
        codeRequired: "الرمز مطلوب.",
        invalidLevel: "اختر مستوى.",
        invalidType: "اختر نوع الموقع.",
        parentWarehouse: "الموقع الأعلى يتبع مستودعًا آخر.",
        levelOrder: "يجب أن يكون المستوى أدنى من مستوى الموقع الأعلى.",
        capacity: "يجب أن تكون قيم السعة صفرًا أو أكثر."
      },
      lotErrors: {
        lotRequired: "هذا المنتج متتبع بالدفعات؛ أدخل رقم الدفعة.",
        serialDuplicate: "يجب ألا تتكرر الأرقام التسلسلية.",
        serialCount: "أدخل رقمًا تسلسليًا لكل وحدة.",
        productionDateRequired: "تاريخ الإنتاج مطلوب لهذا المنتج.",
        expiryRequired: "تاريخ الانتهاء مطلوب لهذا المنتج.",
        expiryBeforeProduction: "يجب أن يكون تاريخ الانتهاء بعد تاريخ الإنتاج."
      },
      receiptErrors: {
        PRODUCT_NOT_FOUND: "المنتج غير موجود.",
        UNKNOWN_UOM: "لا توجد هذه الوحدة للمنتج.",
        OVER_TOLERANCE: "تجاوز الأمر حد التفاوت (الحد الأقصى {{max}}، ±{{pct}}%).",
        QUARANTINE_LOCATION_REQUIRED: "يحتاج هذا المنتج إلى موقع حجر في المستودع.",
        LOCATION_CAPACITY: "سيتجاوز الموقع سعته."
      },
      toleranceStatus: { complete: "مكتمل", under: "ناقص", over: "زائد (ضمن التفاوت)", overLimit: "تجاوز الحد" },
      settings: {
        title: "الوحدات والجودة وقواعد الدفعات",
        altUnits: "وحدات بديلة (الأساس: {{base}})",
        factor: "كم {{base}}",
        addUnit: "إضافة وحدة",
        qualityRequired: "فحص جودة عند الاستلام (حجر)",
        tolerance: "تفاوت الاستلام %",
        tracking: "التتبع",
        trackingModes: { none: "بدون", lot: "دفعة", serial: "تسلسلي" },
        shelfLifeDays: "مدة الصلاحية (أيام)",
        shelfLife: "مدة الصلاحية {{days}} يومًا",
        requireProductionDate: "تاريخ الإنتاج إلزامي",
        requireExpiry: "تاريخ الانتهاء إلزامي",
        errors: {
          required: "اختر وحدة.",
          duplicate: "يمكن إضافة كل وحدة مرة واحدة.",
          positive: "أدخل قيمة أكبر من صفر.",
          range: "يجب أن يكون التفاوت بين 0 و100.",
          failed: "تعذر الحفظ."
        }
      },
      variants: {
        title: "المتغيرات",
        attribute: "الخاصية (مثل اللون)",
        values: "القيم مفصولة بفواصل",
        addAttribute: "إضافة خاصية",
        generate: "إنشاء {{count}} متغيرات",
        matrix: "{{count}} تركيبة",
        tooMany: "عدد التركيبات كبير جدًا (الحد 200).",
        none: "لا توجد متغيرات بعد.",
        variant: "المتغير",
        total: "الإجمالي",
        variantOf: "متغير من",
        failed: "تعذر إنشاء المتغيرات."
      },
      migration: {
        pending: "لا يزال {{updates}} موقعًا يستخدم حقول الممر / الرف / الخانة القديمة؛ سيتم إنشاء {{creates}} سجلًا أعلى.",
        run: "النقل إلى التسلسل الهرمي",
        done: "تم إنشاء التسلسل الهرمي: {{created}} جديد، {{updated}} منقول.",
        legacy: "تنسيق قديم"
      },
      overview: {
        title: "المخزون الفوري",
        subtitle: "المخزون الفعلي والمحجوز والمتاح والمحجور وفي الطريق حسب المستودع والموقع",
        search: "SKU أو الاسم",
        allWarehouses: "كل المستودعات",
        allTypes: "كل أنواع المواقع",
        refresh: "تحديث",
        empty: "لا يوجد مخزون.",
        byProduct: "حسب المنتج"
      },
      transit: {
        title: "في الطريق",
        none: "لا شيء في الطريق.",
        route: "المسار",
        receive: "استلام",
        received: "تم استلام التحويل."
      },
      quarantine: {
        title: "قرار الجودة",
        decide: "اتخاذ قرار",
        inspection: "الفحص",
        noInspection: "لا يوجد فحص لهذا المنتج — سجّل فحصًا أولًا.",
        decision: "القرار",
        release: "الإفراج إلى التخزين",
        return: "النقل إلى المرتجعات",
        apply: "تطبيق",
        done: { release: "تم الإفراج إلى التخزين.", return: "تم النقل إلى موقع المرتجعات." },
        errors: {
          invalidDecision: "اختر قرارًا.",
          inspectionRequired: "اختر الفحص الذي يستند إليه القرار.",
          inspectionRejected: "تم رفض سجل الفحص هذا.",
          inspectionProduct: "الفحص يخص منتجًا آخر.",
          inspectionLot: "الفحص يخص دفعة أخرى.",
          inspectionNotPassed: "لا يفرج عن المخزون إلا فحص ناجح.",
          inspectionPassed: "لا يمكن لفحص ناجح إرسال المخزون إلى المرتجعات.",
          NOT_QUARANTINE_LOCATION: "المخزون ليس في موقع حجر.",
          STORAGE_LOCATION_REQUIRED: "يحتاج المستودع إلى موقع تخزين.",
          RETURNS_LOCATION_REQUIRED: "يحتاج المستودع إلى موقع مرتجعات."
        }
      },
      trace: {
        title: "التتبع والاستدعاء",
        subtitle: "تتبع دفعة أو رقمًا تسلسليًا من المورد إلى العميل واعرض العملاء المتأثرين",
        anyProduct: "أي منتج",
        run: "تتبع",
        open: "تتبع",
        queryRequired: "أدخل رقم دفعة أو رقمًا تسلسليًا.",
        empty: "لا توجد سجلات لهذه الدفعة / الرقم.",
        backward: "للخلف",
        forward: "للأمام",
        suppliers: "الموردون",
        receipts: "استلامات البضائع",
        quality: "فحوصات الجودة",
        inputLots: "دفعات المدخلات",
        lotCard: "الدفعة",
        production: "الإنتاج",
        timeline: "الخط الزمني",
        outputLots: "استُخدمت في دفعات",
        packaging: "التغليف",
        shipments: "الشحنات",
        recallTitle: "قائمة الاستدعاء",
        noCustomers: "لم تُشحن هذه الدفعة إلى عملاء.",
        stages: {
          receipt: "استلام",
          quality: "جودة",
          production: "إنتاج",
          consumption: "استهلاك",
          packaging: "تغليف",
          shipment: "شحن"
        },
        cols: {
          customer: "العميل",
          contact: "جهة الاتصال",
          email: "البريد الإلكتروني",
          phone: "الهاتف",
          address: "العنوان",
          quantity: "الكمية",
          orders: "الطلبات",
          serials: "الأرقام التسلسلية",
          lastShipDate: "آخر شحنة"
        }
      }
    }
  }
};

const merge = (a, b) => Object.keys(b).reduce((out, k) => {
  out[k] = a[k] && typeof a[k] === "object" && typeof b[k] === "object" ? merge(a[k], b[k]) : b[k];
  return out;
}, { ...a });

export const stockLocales = Object.fromEntries(
  Object.entries(baseLocales).map(([lang, pack]) => [lang, merge(merge(merge(merge(merge(merge(merge(merge(merge(merge(merge(pack, inventoryRulesLocales[lang]), organizationLocales[lang]), planningLocales[lang]), complianceLocales[lang]), portabilityLocales[lang]), warehouseReportLocales[lang]), scanFillLocales[lang]), aiAnalysisLocales[lang]), companyLicenseLocales[lang]), settingsTextLocales[lang]), coreTextLocales[lang])])
);
